<script>

    import {fly} from 'svelte/transition';
    import {to, API} from "@API";
    import {getContext, createEventDispatcher, tick} from "svelte";
    import StripePayment from "@components/StripePayment.svelte";
    import ChooseBeltSize from "@components/ChooseBeltSize.svelte";
    import Loader from "@components/Loader.svelte";
    import { Country, State, City }  from 'country-state-city';
    import ProductInfo from "@components/ProductInfo.svelte"
    import PlaceAutocomplete from "@components/PlaceAutocomplete.svelte";
    import ConfirmationModal from "@components/ConfirmationModal.svelte";

    let OrgFinder = API.service('organizations/find');
    const BindUserPurchase = API.service('shop/bind_user_purchase/');
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    let {org, product, shopData, location, user, matchkey} = getContext("SHOP");

    let OrderService = API.service('/shop/orders/createUserOrder');
    OrderService.host(org.api);

    const countries = [{value: 'US', label: 'United States'}, {value: 'CA', label: 'Canada'}, {value: 'GB', label: 'United Kingdom'}];
    const defaultCountry = $location && $location.country || org.countrycode;

    export let customerData = {}
    let loading = false;
    let error = false;
    let response;
    let paymentIntentData;
    let orgValidated = false;
    let orgError = null;
    let orgLoading = false;
    let sameEmailError = false;
    let productPrice;
    let addressError;
    let modal, showWarning;
    let manualState = false;

    export let showBackOnOne = true;
    export let showB2BForm = false;
    let validating = false;

    let currentStep = 0;
    let stepLabel = 'Customer Information';
    let lockEmail = false;
    let addingCardDetails = false;

    let steps = [
        "Customer Information",
        'Shipping Information',
        ...($shopData.remove_belt || !product.allow_belt_purchase ? [] : ['Choose Belt'])
    ]

    if ($shopData.coupon && $shopData.coupon.percent_off == 100 && $shopData.coupon.duration != 'forever') {
        console.log('entering coupon flow..')
        // setup different steps based on coupon flow if 100% off
        steps.push('Add Card Details')
        steps.push('Confirm Subscription')
    } else {
        steps.push('Finalize Payment')
    }

    let dispatch = createEventDispatcher();

    let formErrors = [];

    let PurchaseIntentService = API.service('shop/:product_slug/create_payment_intent');
    let PaymentIntentSetup = API.service('shop/:product_slug/create_card_intent');
    let VerifyAddress = API.service('fedex/address_verify');
    let CreateUserSubscription = API.service('users/:guid/assign-subscription');
    CreateUserSubscription.host(org.api + "/");

    if(window.posthog){
      window.posthog.capture('store-customer-info', {value: true});
    }

    let cities = [];
    let states = [];

    // if we have a user in the template.. then use it, because we are coming from an invite
    if (user) {
        customerData.firstname = user.firstname;
        customerData.lastname = user.lastname;
        customerData.email = user.email;
        lockEmail = true;
    }

    function onBack() {
        dispatch('back')
    }

    async function validateCustomerInfo() {
        
        loading = true;
        await tick();
        formErrors = {};
        sameEmailError = false;
        manualState = false;

        let validationMap = {
            firstname: true,
            lastname: true,
            email: true
        }

        if ($shopData.productType === "couple") {
            validationMap['firstname2'] = true
            validationMap['lastname2'] = true
            validationMap['email2'] = true
        }

        for (let key of Object.keys(validationMap)) {
            if (!customerData[key]) {
                formErrors[key] = true
            }
            if (key.includes("email") && !emailRegex.test(customerData[key])) {
                formErrors[key] = true;
            }
            if (key === 'email2' && customerData['email'] === customerData['email2']) {
                formErrors[key] = true;
                sameEmailError = true;
            }
        }

        loading = false;
        const orgNamespace = [org.api.split('//')[1].split('.')[0]]; //org, needs a better way to get the namespace
        const loc = org.locations.find(l => $shopData.location === l.guid);

        let namespace = `${orgNamespace}`;
        if (loc){
          namespace += '-'+loc.namespace;
        }
        if (Object.keys(formErrors).length === 0) {
            if (steps[currentStep] === "Customer Information") {
                const tags = {
                    active_lead: true,
                    [product.slug]: true,
                    [namespace]: true,
                    [orgNamespace]: true,
                    env: process.env.SENTRY_ENV
                };
                //await window.Marketing.addUser({firstname: customerData.firstname, lastname: customerData.lastname, email:customerData.email, tags})
            }
            nextStep(currentStep + 1)
        }
    }

    // after we have added the card.. we have to call the server to create the Subscription
    // so we can show the prices for the user to finalize.
    // only used for 100% off coupons...
    async function handleAddCard({detail}) {
        console.log('card created', detail)
        addingCardDetails = true;
        let {subscription, paymentIntent} = detail;

        let data = {
            org: org.guid,
            location: $shopData.location,
            subscription: subscription,
            purchase: paymentIntent,
            product: product,
            service_provider: $shopData.serviceProvider,
            ...(matchkey ? {invite_key: matchkey.invite_key} : {})
        }

        if (!$shopData.remove_belt) {

            let belts = $shopData.beltSizes.map(b => {
                return {qty: 1, sku: b}
            });

            data = {
                ...data,
                ...{
                    needs_belt: !$shopData.remove_belt,
                    pickup_location: $shopData.pick_up_location,
                    equipment: [{qty: 1, sku: 'electrodes-50'}, ...belts]
                }
            }
        }

        let [bindingError, response] = await to(BindUserPurchase.post(data));

        if (bindingError) {
            error = "An error occurred processing your purchase. Please reach out to info@oto.coach for support.";
            return
        }

        if (response) {
            productPrice = response;
            nextStep(currentStep + 1)
        }

    }

    function completeProcess() {
        dispatch('completeSkipBind', true)
    }

    function validateForm() {
        formErrors = {};

        let validationMap = {
            street: true,
            city: true,
            country: true,
            zip: true,
            phonenumber: true,
            state: true,
        }

        for (let key of Object.keys(validationMap)) {
            if (!customerData[key]) {
                if (['street', 'city', 'country', 'zip', 'state'].includes(key)) {
                    formErrors.address = [...(formErrors.address || []), key];
                } else {
                    formErrors[key] = true;
                }
            }
        }
    }

    async function purchase() {

        console.log("Purchase....")

        if (loading) return;

        loading = true;
        error = false;
        let responseError, response;
        validateForm();

        if (Object.keys(formErrors).length) {
            loading = false;
            return
        }

        if(window.posthog){
          window.posthog.capture('store-saving-information', {value: true});
        }

        [responseError, response] = await to(VerifyAddress.post({
            "streetLines": [customerData.street],
            "city": customerData.city,
            "stateOrProvinceCode": customerData.state,
            "postalCode": customerData.zip,
            "countryCode": customerData.country
        }));


        if (response && response.response.errors) {
            console.log("System error... maybe we just let the address continue")
        } else if (response) {
            let validateMessage = response.response.output.resolvedAddresses[0].customerMessages.length && response.response.output.resolvedAddresses[0].customerMessages[0].code
            if (validateMessage === 'STANDARDIZED.ADDRESS.NOTFOUND') {
                error = "We could not validate your address. Please make sure all the details are correct.";
                loading = false;
                return;
            }
            if(window.posthog){
              window.posthog.capture('store-address-verify', {message: validateMessage});
            }
        }

        // Complimentary subscription only for legacy research invite matchkeys (study flows use study enrollment bind).
        if (matchkey && matchkey.researchInvite) {

            [responseError, response] = await to(CreateUserSubscription.post({
                customer_details: customerData,
                subscription: product.slug,
                guid: matchkey.user,
                endtime: matchkey.subscriptionEndTime,
                is_couple: $shopData.productType === "couple",
                matchkey: matchkey.invite_key
            }));

            if ($shopData.wants_belt) {
                let belts = $shopData.beltSizes.map(b => {
                    return {qty: 1, sku: b}
                });

                let [error, response] = await to(OrderService.post({
                    name: "TFC Hardware Bundle",
                    user: matchkey.user,
                    location: $shopData.location,
                    equipment: [{qty: 1, sku: 'electrodes-50'}, ...belts],
                    match_key: matchkey.invite_key
                }));
            }

            loading = false;
            if (responseError) {
                error = "Could not complete purchase. Please contact OTO support at support@oto.coach"
            } else {
                dispatch('completeSkipBind', response)
            }
        } else {
            // if the coupon is 100% off, then we have to setup a intent to collect
            // the credit card details instead of a payment intent
            const args = {
                product_slug: product.slug,
                customer_details: customerData,
                organization: org.guid,
                coupon: $shopData.coupon ? $shopData.coupon.id : null,
                is_couple: $shopData.productType === "couple",
                wants_belt: !$shopData.remove_belt, // inverse of this
                belts: JSON.stringify($shopData.beltSizes),
                pickup_location: $shopData.pick_up_location,
                payment_interval: $shopData.payment_interval,
            }
            // add here so we can store to meta data of sub
            if($shopData.serviceProvider) {
              args.service_provider = $shopData.serviceProvider.guid
            }
            if ($shopData.price) {
                let {base_price, ...rest} = $shopData.price;
                if (rest.fakeTier) {
                    args.price = {base_price: base_price}
                } else {
                    args.price = {tier: rest, base_price: base_price}
                }
            }
            if ($shopData.coupon && $shopData.coupon.percent_off === 100) {
                alert("This coupon is invalid. Please contact support")
                return;
                [responseError, response] = await to(PaymentIntentSetup.post(args));

                loading = false;

                if (response) {
                    paymentIntentData = {
                        payment_intent: response.intent,
                        client_secret: response.intent.client_secret
                    }
                    nextStep(currentStep + 1);
                    if(window.posthog){
                      window.posthog.capture('store-customer-onboarding-complete', {value: true});
                    }

                } else {
                    error = "Could not complete purchase. Please contact OTO support at support@oto.coach"
                }
            } else {
                [responseError, response] = await to(PurchaseIntentService.post(args));

                loading = false;

                if (response) {
                    console.log("Payment intent created");
                    paymentIntentData = response;
                    productPrice = response;
                    nextStep(currentStep + 1);
                    if(window.posthog){
                      window.posthog.capture('store-customer-onboarding-complete', {value: true});
                    }
                } else {
                    error = "Could not complete purchase. Please contact OTO support at support@oto.coach"
                }
            }

        }

        console.log(error, response)
    }

    function nextStep(step) {
        currentStep = step;
        stepLabel = steps[step];

        if(window.posthog){
          window.posthog.capture('store-customer-onboarding', {step: step});
        }
    }

    function prevStep(step) {
        console.log("Proceed to prev step", step)
        currentStep = step;
        stepLabel = steps[step];

        if(window.posthog){
          window.posthog.capture('store-customer-onboarding', {step: step});
        }
    }

    // if we are done with shipping we either go to purchase or go to the belt picker
    async function validateShippingInformation() {
        validateForm();
        if (Object.keys(formErrors).length) {
            loading = false;
            if (formErrors.address && formErrors.address.length === 1 && formErrors.address[0] === 'state') {
                showWarning = true;
            }
            return;
        }
        if (customerData.phonenumber) {
            //await window.Marketing.tagUser({key: 'phone', value: customerData.phonenumber});
        }
        
        if (steps[currentStep + 1] === 'Choose Belt') {
            nextStep(currentStep + 1);
        } else {
            purchase()
        }
    }

    async function handleSendSize({detail}) {
        $shopData.beltSizes = detail.belts;
        if (detail.billingAddress) {
            customerData = {...customerData, ...detail.billingAddress}
        }
        await purchase();
    }

    function customerAddressSelected({detail}) {
        if(detail) {
            customerData.zip = detail.postal_code;
            customerData.state = detail.state;
            customerData.city = detail.city;
            customerData.street = detail.line1;
            customerData.country = detail.country;
        }else{
            customerData.zip = '';
            customerData.state = '';
            customerData.city = '';
            customerData.street = '';
            customerData.country = '';
        }
    }

    function handleConfirmState() {
        manualState = true;
        validateShippingInformation();
        handleCloseWarning();
    }

    $: customerData && customerData.country, states = State.getStatesOfCountry(customerData.country);
    
    function handleCloseWarning() {
        modal.state('idle')
        showWarning = false;
    }

</script>

<style lang="scss">
  input.error {
    animation: shake 0.82s cubic-bezier(.36, .07, .19, .97) both;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
    border-color: red !important;
  }
  select.error {
    animation: shake 0.82s cubic-bezier(.36, .07, .19, .97) both;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
    border-color: red !important;
  }

  @keyframes shake {
    10%, 90% {
      transform: translate3d(-1px, 0, 0);
    }

    20%, 80% {
      transform: translate3d(2px, 0, 0);
    }

    30%, 50%, 70% {
      transform: translate3d(-4px, 0, 0);
    }

    40%, 60% {
      transform: translate3d(4px, 0, 0);
    }
  }

  .info-holder {
    max-width: 420px;
    margin: 0 auto;
    @media screen and (max-width: 900px) {
      margin: 30px auto 0;
    }
  }

  .shipping-info {
    max-width: 540px;
    margin: 0px auto 0;
    @media screen and (max-width: 900px) {
      margin: 30px auto 0;
      width: 100%;
      padding-left:30px;
      padding-right:30px;
    }
  }

  .transition-container {
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;

    @media screen and (max-width:1200px){
      @apply m-auto
    }
  }

  .transition-container > * {
    grid-row: 1;
    grid-column: 1;
  }

  .stripe-holder {
    max-width: 550px;
    margin: 0 16px;
    @media screen and (max-width: 900px) {
      margin:30px auto 0;
    }
  }
</style>

<div class="transition-container max-w-4xl px-4 xl:px-16">

    {#if stepLabel === 'Customer Information'}
        <div class="info-holder">
            {#if showBackOnOne}
                <div class="back cursor-pointer text-left" on:click={onBack}><i
                        class="fa-sharp fa-solid fa-arrow-left mr-2"></i> Back
                </div>
            {/if}
            <div class="customer-form font-bold text-gray-600  text-left">
                Account Information
            </div>
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-3">
                    <label for="first-name" class="block text-sm font-medium text-gray-700">First name</label>
                    <div class="mt-1">
                        <input type="text" name="first-name" id="first-name" bind:value={customerData.firstname}
                               class:error={formErrors['firstname']} autocomplete="given-name"
                               class="block w-full rounded-md {formErrors['firstname'] ? 'border-red-400' : 'border-gray-300' } shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <label for="last-name" class="block text-sm font-medium text-gray-700">Last name</label>
                    <div class="mt-1">
                        <input type="text" name="last-name" id="last-name" bind:value={customerData.lastname}
                               class:error={formErrors['lastname']} autocomplete="family-name"
                               class="block w-full rounded-md {formErrors['lastname'] ? 'border-red-400' : 'border-gray-300' } shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <label for="email" class="block text-sm font-medium text-gray-700">Email address</label>
                    <div class="mt-1">
                        <input disabled={lockEmail} id="email" name="email" type="email" autocomplete="email"
                               bind:value={customerData.email} class:error={formErrors['email']}
                               class="block w-full rounded-md {formErrors['email'] ? 'border-red-400' : 'border-gray-300' } {lockEmail ? 'opacity-50 cursor-not-allowed' : ''} shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <label for="email" class="block text-sm font-medium text-gray-700">Gender</label>
                    <div class="relative">
                        <select
                                name="gender1"
                                bind:value={customerData.gender}
                                class="block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                            <option value="M" selected>Male</option>
                            <option value="F">Female</option>
                        </select>
                    </div>
                    <span class="text-gray-600" style="font-size: 11px;">* This is important so the system can process the proper physiology markers</span>
                </div>
                <!--{#if showB2BForm}-->
                <!--<div class="sm:col-span-6">-->
                <!--  <label for="email" class="block text-sm font-medium text-gray-700">Organization Name</label>-->
                <!--  <div class="mt-1 relative">-->
                <!--    <input type="text" on:blur={validateOrganization} autocomplete="off" bind:value={customerData.organization_name} class:error={formErrors['organization_name']} class="block w-full rounded-md {formErrors['organization_name'] ? 'border-red-400' : 'border-gray-300' } shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm pr-10">-->
                <!--    {#if orgError}-->
                <!--      <p class="bg-red-50 px-3 py-3 rounded-md text-center text-sm text-red-500 font-bold">{orgError}</p>-->
                <!--    {/if}-->
                <!--    {#if orgValidated}-->
                <!--      <i class="fa-solid fa-circle-check absolute right-3 text-green-400" style="top: 10px"></i>-->
                <!--    {/if}-->
                <!--    {#if orgLoading}-->
                <!--      <Loader color="black" size={25} position="absolute" style="right:10px; top:7px"/>-->
                <!--    {/if}-->
                <!--  </div>-->
                <!--</div>-->
                <!--{/if}-->
            </div>
            {#if $shopData.productType === 'couple'}
                <div class="relative mt-6">
                    <div class="absolute inset-0 flex items-center">
                        <div class="w-full border-t border-gray-300"></div>
                    </div>
                    <div class="relative flex justify-center text-sm">
                        <span class="bg-white px-2 text-gray-500">Second Account</span>
                    </div>
                </div>
                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div class="sm:col-span-3">
                        <label for="first-name" class="block text-sm font-medium text-gray-700">First name</label>
                        <div class="mt-1">
                            <input type="text" name="first-name2" id="first-name2" bind:value={customerData.firstname2}
                                   class:error={formErrors['firstname2']} autocomplete="given-name"
                                   class="block w-full rounded-md {formErrors['firstname2'] ? 'border-red-400' : 'border-gray-300' } shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <label for="last-name" class="block text-sm font-medium text-gray-700">Last name</label>
                        <div class="mt-1">
                            <input type="text" name="last-name2" id="last-name2" bind:value={customerData.lastname2}
                                   class:error={formErrors['lastname2']} autocomplete="family-name"
                                   class="block w-full rounded-md {formErrors['lastname2'] ? 'border-red-400' : 'border-gray-300' } shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <label for="email2" class="block text-sm font-medium text-gray-700">Email address</label>
                        <div class="mt-1">
                            <input id="email2" name="email2" type="email" autocomplete="email"
                                   bind:value={customerData.email2} class:error={formErrors['email2']}
                                   class="block w-full rounded-md {formErrors['email2'] ? 'border-red-400' : 'border-gray-300' } shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                        </div>
                        {#if sameEmailError}
                            <p class="text-xs text-red-500">Please use different emails for both accounts</p>
                        {/if}
                    </div>
                    <div class="sm:col-span-6">
                        <label for="gender2" class="block text-sm font-medium text-gray-700">Gender</label>
                        <div class="relative">
                            <select
                                    id="gender2"
                                    name="gender2"
                                    bind:value={customerData.gender2}
                                    class="block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                                <option value="M">Male</option>
                                <option value="F" selected>Female</option>
                            </select>
                        </div>
                        <span class="text-gray-600" style="font-size: 11px;">* This is important so the system can process the proper physiology markers</span>

                    </div>
                </div>
            {/if}
            <button type="button" on:click={validateCustomerInfo}
                    class="mt-5 w-full items-center rounded-md border border-transparent bg-black px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">{loading ? "Please wait..." : "Save Account Details"}</button>
        </div>

    {:else if stepLabel === 'Shipping Information'}
        <div class="shipping-info" style="min-width:358px">
            <div class="back text-left cursor-pointer" on:click={e=>{prevStep(currentStep -= 1)}}><i
                    class="fa-sharp fa-solid fa-arrow-left mr-2"></i> Back
            </div>
            <div class="customer-form font-bold text-gray-600 text-left">
                Shipping Information
            </div>
            <div class="">
                <div class="mb-6">
                    <PlaceAutocomplete worldwide class="mt-4" on:select={customerAddressSelected} placeholder="Enter shipping address" country={defaultCountry} bind:error={formErrors['address']}/>
                    {#if manualState && customerData.state}
                        <p class="text-sm mt-2">Selected Province: {customerData.state}</p>
                    {/if}
                </div>
                <div class="md:col-span-2">
                    <label for="phone" class="block text-sm font-medium text-gray-700">Phone Number</label>
                    <div class="mt-1">
                        <input type="text" name="phone" id="phone" bind:value={customerData.phonenumber}
                               class:error={formErrors['phonenumber']} autocomplete="phone"
                               class="block w-full rounded-md {formErrors['phonenumber'] ? 'border-red-400' : 'border-gray-300' } shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                    </div>
                </div>
            </div>
            <button type="button" on:click={validateShippingInformation}
                    class="mt-5 w-full items-center rounded-md border border-transparent bg-black px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">{loading ? "Please wait..." : "Save Account Details"}</button>

        </div>
    {:else if stepLabel === 'Choose Belt'}
        <div class="px-4 shipping-info">
            <div class="back text-left cursor-pointer" on:click={e=>{prevStep(currentStep -= 1)}}><i
                    class="fa-sharp fa-solid fa-arrow-left mr-2"></i> Back
            </div>
            <div class="customer-form font-semibold text-black text-left">
                <p class="w-full text-center text-2xl">
                    Choose Belt Size
                </p>
                <p class="text-center w-full text-lg text-charcoal-dark mt-5 mb-6">
                    Please measure ONLY THE FRONT of the chest
                </p>
            </div>
            <ChooseBeltSize context="SHOP" settings={$shopData} on:beltSize={handleSendSize}/>
        </div>
    {:else if stepLabel === 'Add Card Details'}
        {#if paymentIntentData}
            <div class="stripe-holder">
                {#if !addingCardDetails}
                    <div class="back text-left mb-3 cursor-pointer" on:click={()=>{prevStep(currentStep -= 1)}}><i
                            class="fa-sharp fa-solid fa-arrow-left mr-2"></i> Back
                    </div>
                    <p class="my-3 bg-gray-50 rounded-md px-3 py-2">
                        You have applied a 100% off coupon.<br>
                        Please provider payment details for future invoices after your discount has expired in
                        {$shopData.coupon.duration_in_months} month(s)
                    </p>
                {/if}
                <StripePayment intent={paymentIntentData} isCard={true} on:complete={handleAddCard}/>
            </div>
            {#if addingCardDetails}
                <div class="purchase-success">
                    <i class="fa-duotone text-5xl fa-truck-ramp-box"></i>
                    <span class="text-xl mt-3">
            Please wait.. We are processing your order.
          </span>
                    <p class="mt-3 text-gray-400 text-base text-center">This may take a minute.</p>
                </div>
            {/if}
        {:else}
            Please wait...
        {/if}
    {:else if stepLabel === 'Confirm Subscription'}
        <!-- used for 100% flow  -->
        <div class="purchase-success">
            <i class="fa-duotone text-5xl fa-badge-check" style="color: green;"></i>
            <span class="text-xl mt-3"> Your account has been created. </span>
            <p class="mt-3 text-gray-400 text-base text-center">Review the details to the left for your future invoices.</p>
            <button type="button" on:click={completeProcess} class="mt-3 w-full items-center rounded-md border border-transparent bg-black px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                Complete
            </button>
        </div>
    {:else if stepLabel === 'Finalize Payment' }
        {#if paymentIntentData}
            <div class="stripe-holder">
                <div class="back mb-3 text-left cursor-pointer" on:click={()=>{prevStep(currentStep -= 1)}}><i
                        class="fa-sharp fa-solid fa-arrow-left mr-2"></i> Back
                </div>
                <ProductInfo {product} stripeData={productPrice}/>
                <StripePayment intent={paymentIntentData} on:complete/>
            </div>
        {:else}
            Please wait...
        {/if}

    {/if}

</div>
{#if error}
    <p class="mt-4 text-sm p-3 bg-red-50 text-red-400 font-bold rounded-md text-center w-full">{error}</p>
{/if}
<ConfirmationModal bind:this={modal} on:confirm={handleConfirmState} type="confirm" on:close={handleCloseWarning}
                   show={showWarning} title="Select State" actionButtonLabel="Continue" dismissButtonLabel="Cancel">
    <svelte:fragment slot="message">
        <p class="text-black">We didn't find a province in the address. Please type the province for your shipment.</p>
        <input class="mt-6 rounded-md" type="text" name="state" id="state" bind:value={customerData.state} />
    </svelte:fragment>
</ConfirmationModal>